import React, { useRef, useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';
import { themeAware, getThemeValue } from '../../theme/theme-utils';

// Chart types
export type ChartType = 'bar' | 'line' | 'pie' | 'scatter';

// Data point interface
export interface DataPoint {
  label: string;
  value: number;
  color?: string;
}

// Dataset interface
export interface Dataset {
  label: string;
  data: number[];
  backgroundColor?: string | string[];
  borderColor?: string;
  borderWidth?: number;
}

// Chart data interface
export interface ChartData {
  labels: string[];
  datasets: Dataset[];
}

// Chart props interface
export interface ChartProps {
  /**
   * Type of chart to render
   */
  type: ChartType;
  /**
   * Chart title
   */
  title?: string;
  /**
   * Chart description
   */
  description?: string;
  /**
   * Labels for the chart (x-axis for bar/line, labels for pie)
   */
  labels?: string[];
  /**
   * Datasets for the chart
   */
  datasets?: Dataset[];
  /**
   * Complete chart data (alternative to separate labels and datasets)
   */
  data?: ChartData;
  /**
   * Height of the chart in pixels
   */
  height?: number;
  /**
   * Width of the chart in pixels or percentage
   */
  width?: number | string;
  /**
   * Whether to show the legend
   */
  showLegend?: boolean;
  /**
   * Whether to show the axes
   */
  showAxes?: boolean;
  /**
   * Whether to show grid lines
   */
  showGrid?: boolean;
  /**
   * Whether to animate the chart
   */
  animate?: boolean;
  /**
   * Additional options for the chart
   */
  options?: any;
  /**
   * Callback when a data point is clicked
   */
  onDataPointClick?: (dataPoint: DataPoint, index: number) => void;
  /**
   * Additional class name
   */
  className?: string;
}

// Styled components
const ChartContainer = styled.div<{ $width?: number | string; $height?: number }>`
  width: ${props => typeof props.$width === 'number' ? `${props.$width}px` : props.$width || '100%'};
  height: ${props => props.$height ? `${props.$height}px` : '300px'};
  position: relative;
  margin-bottom: 1rem;
`;

const ChartTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  ${themeAware(theme => `
    color: ${getThemeValue(theme, t => t.colors.neutral[900], '#212121')};
  `)}
`;

const ChartDescription = styled.p`
  font-size: 0.875rem;
  margin-bottom: 1rem;
  ${themeAware(theme => `
    color: ${getThemeValue(theme, t => t.colors.neutral[600], '#757575')};
  `)}
`;

const SVGContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 1rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 1rem;
  margin-bottom: 0.5rem;
`;

const LegendColor = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  background-color: ${props => props.$color};
  margin-right: 0.5rem;
  border-radius: 2px;
`;

const LegendLabel = styled.span`
  font-size: 0.75rem;
  ${themeAware(theme => `
    color: ${getThemeValue(theme, t => t.colors.neutral[600], '#757575')};
  `)}
`;

/**
 * Chart component for data visualization using D3.js
 */
const Chart = forwardRef<any, ChartProps>(({
  type,
  title,
  description,
  labels = [],
  datasets = [],
  data,
  height = 300,
  width = '100%',
  showLegend = true,
  showAxes = true,
  showGrid = true,
  animate = true,
  options = {},
  onDataPointClick,
  className,
}, ref) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartData, setChartData] = useState<ChartData>({ labels: [], datasets: [] });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  // Use data prop if provided, otherwise use labels and datasets
  useEffect(() => {
    if (data) {
      setChartData(data);
    } else {
      setChartData({ labels, datasets });
    }
  }, [data, labels, datasets]);
  
  // Measure container dimensions
  useEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setDimensions({ width, height });
    }
  }, []);
  
  // Expose methods to parent components via ref
  useImperativeHandle(ref, () => ({
    // Method to export chart as base64 image
    toBase64Image: (format: 'png' | 'jpeg' = 'png') => {
      if (!svgRef.current) return '';
      
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const svg = svgRef.current;
      const box = svg.getBoundingClientRect();
      canvas.width = box.width;
      canvas.height = box.height;
      
      // Draw SVG on canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';
      
      // Convert SVG to data URL
      const data = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      
      // Create image from SVG
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
      };
      img.src = url;
      
      // Return base64 data URL
      return canvas.toDataURL(`image/${format}`, 1.0);
    },
    
    // Method to get chart data
    getData: () => chartData,
    
    // Method to update chart data
    updateData: (newData: ChartData) => {
      setChartData(newData);
    }
  }));
  
  // Render chart using D3.js
  useEffect(() => {
    if (!svgRef.current || !chartData.labels.length || !chartData.datasets.length || !dimensions.width) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous chart
    
    const margin = { top: 20, right: 20, bottom: 40, left: 50 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;
    
    // Create chart container with margins
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Get color scale for datasets
    const getColor = (index: number, dataset: Dataset) => {
      if (dataset.backgroundColor) {
        if (Array.isArray(dataset.backgroundColor)) {
          return dataset.backgroundColor[index % dataset.backgroundColor.length];
        }
        return dataset.backgroundColor;
      }
      // Default colors if not specified
      const colors = [
        '#4C51BF', '#48BB78', '#ED8936', '#4299E1', '#9F7AEA',
        '#F56565', '#38B2AC', '#ECC94B', '#667EEA', '#FC8181'
      ];
      return colors[index % colors.length];
    };
    
    // Render different chart types
    switch (type) {
      case 'bar':
        renderBarChart(g, chartData, width, height, getColor, showAxes, showGrid, animate);
        break;
      case 'line':
        renderLineChart(g, chartData, width, height, getColor, showAxes, showGrid, animate);
        break;
      case 'pie':
        renderPieChart(g, chartData, width, height, getColor, animate);
        break;
      case 'scatter':
        renderScatterChart(g, chartData, width, height, getColor, showAxes, showGrid, animate);
        break;
    }
    
    // Add click handler for data points
    if (onDataPointClick) {
      svg.selectAll('.data-point')
        .on('click', (event, d: any) => {
          const index = d.index;
          const dataPoint: DataPoint = {
            label: chartData.labels[index],
            value: d.value,
            color: d.color
          };
          onDataPointClick(dataPoint, index);
        });
    }
    
    // Add custom event handlers from options
    if (options.onClick) {
      svg.on('click', (event) => {
        const elements = svg.selectAll('.data-point').filter(function() {
          const element = d3.select(this);
          const x = parseFloat(element.attr('cx') || '0');
          const y = parseFloat(element.attr('cy') || '0');
          const r = parseFloat(element.attr('r') || '5');
          
          const mouseX = d3.pointer(event)[0];
          const mouseY = d3.pointer(event)[1];
          
          // Check if click is within the data point
          return Math.sqrt(Math.pow(mouseX - x, 2) + Math.pow(mouseY - y, 2)) <= r;
        });
        
        if (elements.size() > 0) {
          const element = elements.nodes()[0];
          const data = d3.select(element).datum();
          options.onClick(event, [{ index: (data as any).index }]);
        }
      });
    }
    
  }, [type, chartData, dimensions, showAxes, showGrid, animate, onDataPointClick, options]);
  
  // Render bar chart
  const renderBarChart = (
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    data: ChartData,
    width: number,
    height: number,
    getColor: (index: number, dataset: Dataset) => string,
    showAxes: boolean,
    showGrid: boolean,
    animate: boolean
  ) => {
    const { labels, datasets } = data;
    
    // Create scales
    const x0 = d3.scaleBand()
      .domain(labels)
      .range([0, width])
      .padding(0.1);
    
    const x1 = d3.scaleBand()
      .domain(datasets.map(d => d.label))
      .range([0, x0.bandwidth()])
      .padding(0.05);
    
    const y = d3.scaleLinear()
      .domain([0, d3.max(datasets, d => d3.max(d.data) || 0) as number])
      .nice()
      .range([height, 0]);
    
    // Add axes if enabled
    if (showAxes) {
      // X axis
      g.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x0))
        .selectAll('text')
        .attr('y', 10)
        .attr('x', -5)
        .attr('dy', '.35em')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end');
      
      // Y axis
      g.append('g')
        .call(d3.axisLeft(y));
    }
    
    // Add grid lines if enabled
    if (showGrid) {
      g.append('g')
        .attr('class', 'grid')
        .call(d3.axisLeft(y)
          .tickSize(-width)
          .tickFormat(() => '')
        )
        .selectAll('line')
        .style('stroke', '#e0e0e0')
        .style('stroke-opacity', '0.7')
        .style('shape-rendering', 'crispEdges');
    }
    
    // Add bars
    datasets.forEach((dataset, i) => {
      const bars = g.selectAll(`.bar-${i}`)
        .data(dataset.data.map((d, j) => ({ value: d, index: j })))
        .enter().append('rect')
        .attr('class', `bar-${i} data-point`)
        .attr('x', (d, j) => (x0(labels[j]) || 0) + (x1(dataset.label) || 0))
        .attr('y', height)
        .attr('width', x1.bandwidth())
        .attr('height', 0)
        .attr('fill', (d, j) => getColor(i, dataset));
      
      // Animate if enabled
      if (animate) {
        bars.transition()
          .duration(1000)
          .attr('y', d => y(d.value))
          .attr('height', d => height - y(d.value));
      } else {
        bars
          .attr('y', d => y(d.value))
          .attr('height', d => height - y(d.value));
      }
    });
  };
  
  // Render line chart
  const renderLineChart = (
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    data: ChartData,
    width: number,
    height: number,
    getColor: (index: number, dataset: Dataset) => string,
    showAxes: boolean,
    showGrid: boolean,
    animate: boolean
  ) => {
    const { labels, datasets } = data;
    
    // Create scales
    const x = d3.scalePoint()
      .domain(labels)
      .range([0, width]);
    
    const y = d3.scaleLinear()
      .domain([0, d3.max(datasets, d => d3.max(d.data) || 0) as number])
      .nice()
      .range([height, 0]);
    
    // Add axes if enabled
    if (showAxes) {
      // X axis
      g.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x))
        .selectAll('text')
        .attr('y', 10)
        .attr('x', -5)
        .attr('dy', '.35em')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end');
      
      // Y axis
      g.append('g')
        .call(d3.axisLeft(y));
    }
    
    // Add grid lines if enabled
    if (showGrid) {
      g.append('g')
        .attr('class', 'grid')
        .call(d3.axisLeft(y)
          .tickSize(-width)
          .tickFormat(() => '')
        )
        .selectAll('line')
        .style('stroke', '#e0e0e0')
        .style('stroke-opacity', '0.7')
        .style('shape-rendering', 'crispEdges');
    }
    
    // Create line generator
    const line = d3.line<{ value: number; index: number }>()
      .x(d => x(labels[d.index]) || 0)
      .y(d => y(d.value));
    
    // Add lines and points
    datasets.forEach((dataset, i) => {
      const dataWithIndex = dataset.data.map((d, j) => ({ value: d, index: j }));
      
      // Add line
      const path = g.append('path')
        .datum(dataWithIndex)
        .attr('fill', 'none')
        .attr('stroke', dataset.borderColor || getColor(i, dataset))
        .attr('stroke-width', dataset.borderWidth || 2)
        .attr('d', line);
      
      // Animate if enabled
      if (animate) {
        const pathLength = path.node()?.getTotalLength() || 0;
        path
          .attr('stroke-dasharray', pathLength)
          .attr('stroke-dashoffset', pathLength)
          .transition()
          .duration(1000)
          .attr('stroke-dashoffset', 0);
      }
      
      // Add points
      const points = g.selectAll(`.point-${i}`)
        .data(dataWithIndex)
        .enter().append('circle')
        .attr('class', `point-${i} data-point`)
        .attr('cx', d => x(labels[d.index]) || 0)
        .attr('cy', d => y(d.value))
        .attr('r', 5)
        .attr('fill', dataset.backgroundColor || getColor(i, dataset))
        .attr('stroke', dataset.borderColor || getColor(i, dataset))
        .attr('stroke-width', 2)
        .style('opacity', 0);
      
      // Animate points if enabled
      if (animate) {
        points.transition()
          .delay((_, j) => j * 100)
          .duration(500)
          .style('opacity', 1);
      } else {
        points.style('opacity', 1);
      }
    });
  };
  
  // Render pie chart
  const renderPieChart = (
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    data: ChartData,
    width: number,
    height: number,
    getColor: (index: number, dataset: Dataset) => string,
    animate: boolean
  ) => {
    const { labels, datasets } = data;
    
    // Use the first dataset for pie chart
    const dataset = datasets[0];
    const pieData = dataset.data.map((value, i) => ({
      label: labels[i],
      value,
      index: i,
      color: Array.isArray(dataset.backgroundColor)
        ? dataset.backgroundColor[i % dataset.backgroundColor.length]
        : getColor(i, dataset)
    }));
    
    // Create pie layout
    const pie = d3.pie<{ label: string; value: number; index: number; color: string }>()
      .value(d => d.value)
      .sort(null);
    
    const radius = Math.min(width, height) / 2;
    
    // Move center of pie to center of svg
    g.attr('transform', `translate(${width / 2},${height / 2})`);
    
    // Create arc generator
    const arc = d3.arc<d3.PieArcDatum<{ label: string; value: number; index: number; color: string }>>()
      .innerRadius(0)
      .outerRadius(radius * 0.8);
    
    // Create arcs
    const arcs = g.selectAll('.arc')
      .data(pie(pieData))
      .enter().append('g')
      .attr('class', 'arc data-point');
    
    // Add slices
    const paths = arcs.append('path')
      .attr('fill', d => d.data.color)
      .attr('stroke', 'white')
      .attr('stroke-width', 2);
    
    // Animate if enabled
    if (animate) {
      paths
        .transition()
        .duration(1000)
        .attrTween('d', function(d) {
          const interpolate = d3.interpolate({ startAngle: 0, endAngle: 0 }, d);
          return function(t) {
            return arc(interpolate(t)) || '';
          };
        });
    } else {
      paths.attr('d', arc);
    }
    
    // Add labels
    arcs.append('text')
      .attr('transform', d => `translate(${arc.centroid(d)})`)
      .attr('dy', '.35em')
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', 'white')
      .style('pointer-events', 'none')
      .text(d => d.data.value > (d3.sum(pieData, d => d.value) * 0.05) ? d.data.label : '');
  };
  
  // Render scatter chart
  const renderScatterChart = (
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    data: ChartData,
    width: number,
    height: number,
    getColor: (index: number, dataset: Dataset) => string,
    showAxes: boolean,
    showGrid: boolean,
    animate: boolean
  ) => {
    const { labels, datasets } = data;
    
    // Create scales
    const x = d3.scaleLinear()
      .domain([0, labels.length - 1])
      .range([0, width]);
    
    const y = d3.scaleLinear()
      .domain([0, d3.max(datasets, d => d3.max(d.data) || 0) as number])
      .nice()
      .range([height, 0]);
    
    // Add axes if enabled
    if (showAxes) {
      // X axis
      g.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(labels.length).tickFormat(i => labels[Math.round(Number(i))] || ''));
      
      // Y axis
      g.append('g')
        .call(d3.axisLeft(y));
    }
    
    // Add grid lines if enabled
    if (showGrid) {
      g.append('g')
        .attr('class', 'grid')
        .call(d3.axisLeft(y)
          .tickSize(-width)
          .tickFormat(() => '')
        )
        .selectAll('line')
        .style('stroke', '#e0e0e0')
        .style('stroke-opacity', '0.7')
        .style('shape-rendering', 'crispEdges');
    }
    
    // Add points
    datasets.forEach((dataset, i) => {
      const points = g.selectAll(`.point-${i}`)
        .data(dataset.data.map((d, j) => ({ value: d, index: j })))
        .enter().append('circle')
        .attr('class', `point-${i} data-point`)
        .attr('cx', d => x(d.index))
        .attr('cy', d => y(d.value))
        .attr('r', 0)
        .attr('fill', dataset.backgroundColor || getColor(i, dataset))
        .attr('stroke', dataset.borderColor || getColor(i, dataset))
        .attr('stroke-width', 2);
      
      // Animate if enabled
      if (animate) {
        points.transition()
          .delay((_, j) => j * 50)
          .duration(500)
          .attr('r', 5);
      } else {
        points.attr('r', 5);
      }
    });
  };
  
  // Generate legend items
  const legendItems = chartData.datasets.map((dataset, i) => {
    const color = dataset.backgroundColor
      ? (Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[0] : dataset.backgroundColor)
      : '#4C51BF';
    
    return {
      label: dataset.label,
      color
    };
  });
  
  return (
    <div className={className}>
      {title && <ChartTitle>{title}</ChartTitle>}
      {description && <ChartDescription>{description}</ChartDescription>}
      <ChartContainer ref={containerRef} $width={width} $height={height}>
        <SVGContainer>
          <svg ref={svgRef} width="100%" height="100%" />
        </SVGContainer>
      </ChartContainer>
      
      {showLegend && legendItems.length > 0 && (
        <Legend>
          {legendItems.map((item, i) => (
            <LegendItem key={i}>
              <LegendColor $color={item.color} />
              <LegendLabel>{item.label}</LegendLabel>
            </LegendItem>
          ))}
        </Legend>
      )}
    </div>
  );
});

Chart.displayName = 'Chart';

export default Chart;